import React, { useCallback } from 'react'
import { X } from 'lucide-react'
import { SelectionModal } from '../../../components/layout/SelectionModal'
import { UserFile } from '../../../service/user'
import { useImageSelection } from '../tabs/StyleImageTab/hooks/useImageSelection'
import { useImageUpload } from '../tabs/StyleImageTab/hooks/useImageUpload'
import { ImageGrid } from '../tabs/StyleImageTab/components/ImageGrid'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'

interface ImageSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onImageSelect: (file: UserFile) => void
  onImageUpload?: (file: File) => void
}

export const ImageSelectionModal: React.FC<ImageSelectionModalProps> = ({
  isOpen,
  onClose,
  onImageSelect,
  onImageUpload,
}) => {
  // 커스텀 훅 사용
  const {
    activeTab,
    selectedFile,
    isDragOver,
    currentFiles,
    isLoading,
    error,
    isFetchingMore,
    hasData,
    currentQuery,
    handleTabChange,
    handleImageClick,
    handleLoadMore,
    handleDragOver,
    handleDragLeave,
    refreshCurrentFiles,
    setIsDragOver,
  } = useImageSelection({ isOpen })

  const { isUploading, handleDrop, handleFileSelect } = useImageUpload({
    onUploadComplete: onImageUpload,
    onTabSwitch: handleTabChange,
    onRefreshFiles: refreshCurrentFiles,
  })

  // 드래그 이벤트 통합
  const handleDragOverWithUpload = useCallback(
    (e: React.DragEvent) => {
      handleDragOver(e)
    },
    [handleDragOver],
  )

  const handleDragLeaveWithUpload = useCallback(
    (e: React.DragEvent) => {
      handleDragLeave(e)
      setIsDragOver(false)
    },
    [handleDragLeave, setIsDragOver],
  )

  const handleDropWithUpload = useCallback(
    (e: React.DragEvent) => {
      setIsDragOver(false)
      handleDrop(e)
    },
    [handleDrop, setIsDragOver],
  )

  const handleSave = useCallback(() => {
    if (selectedFile) {
      onImageSelect(selectedFile)
      onClose()
    }
  }, [selectedFile, onImageSelect, onClose])

  const handleRetry = useCallback(() => {
    currentQuery.refetch()
  }, [currentQuery])

  if (!isOpen) return null

  return (
    <SelectionModal isModal={isOpen}>
      <div
        className={classNames('', {
          'flex h-[600px] w-[816px] flex-col overflow-hidden rounded-[20px] border border-[#EEF1F7] bg-white shadow-[0px_0px_20px_0px_rgba(22,27,35,0.1)]':
            !isMobile,
          'flex h-[100%] w-full flex-col overflow-auto bg-white': isMobile,
        })}
      >
        {/* 헤더 */}
        <div className="flex-shrink-0 px-10 pb-6 pt-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h2 className="text-[24px] font-bold text-[#2E343D]">
                이미지 선택
              </h2>
            </div>

            <button
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center"
            >
              <X size={24} className="text-black" />
            </button>
          </div>
        </div>

        {/* 이미지 그리드 - 스크롤 가능 영역 */}
        <div className="flex-1 overflow-y-auto px-8 py-2">
          {activeTab === 'uploaded' ? (
            <ImageGrid
              files={currentFiles}
              selectedFile={selectedFile}
              onImageClick={handleImageClick}
              isLoading={isLoading}
              error={error}
              hasNextPage={currentQuery.hasNextPage}
              isFetchingMore={isFetchingMore}
              onLoadMore={handleLoadMore}
              onRetry={handleRetry}
              showUploadArea={!!onImageUpload}
              isUploading={isUploading}
              isDragOver={isDragOver}
              onDragOver={handleDragOverWithUpload}
              onDragLeave={handleDragLeaveWithUpload}
              onDrop={handleDropWithUpload}
              onFileSelect={handleFileSelect}
              emptyTitle="아직 업로드한 이미지가 없어요!"
              emptyDescription="이미지는 한 번만 업로드하면 자동 저장! 반복 업로드 없이 내역에서 바로 선택해 사용하세요."
            />
          ) : (
            <ImageGrid
              files={currentFiles}
              selectedFile={selectedFile}
              onImageClick={handleImageClick}
              isLoading={isLoading}
              error={error}
              hasNextPage={currentQuery.hasNextPage}
              isFetchingMore={isFetchingMore}
              onLoadMore={handleLoadMore}
              onRetry={handleRetry}
              showUploadArea={false}
              emptyTitle="아직 생성한 이미지가 없어요!"
              emptyDescription="원하는 스타일을 선택하고 이미지를 업로드하면, AI가 자연스럽게 합성해 완성해 드립니다."
              emptyAction={onClose}
              emptyActionText="이미지 생성"
            />
          )}
        </div>

        {/* 하단 저장 버튼 */}
        <div className="flex-shrink-0 border-t-[1px] border-[#EEF1F7] bg-white p-5 px-10 shadow-[0px_-4px_10px_0px_rgba(0,0,0,0.1)]">
          <button
            onClick={handleSave}
            disabled={!selectedFile}
            className={`flex h-[48px] w-full items-center justify-center rounded-[60px] text-[18px] font-bold transition-colors ${
              selectedFile
                ? 'bg-[#00B7B9] text-white hover:bg-[#00A5A7]'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            저장
          </button>
        </div>
      </div>
    </SelectionModal>
  )
}
